import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000AutoCompleteInput, { U2000AutoCompleteInputResultTable } from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputTextBase from "../../../u2000Core/components/formControls/input/U2000InputTextBase";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000KeyboardKeyHint from "../../../u2000Core/components/common/U2000KeyboardKeyHint";
import U2000AutoCompletePanelResultsBlock, {
  U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS,
} from "../../../u2000Core/components/common/U2000AutoCompletePanelResultsBlock";
import {
  createPanelCriteriaFocusHandler,
  U2000_AUTOCOMPLETE_PANEL_COMPACT,
} from "../../../u2000Core/tools/u2000AutocompletePanelUtils";
import { useU3130FindProjectHandlers, useU3130FindSubprojectHandlers } from "../../U3130UseFindProjectSubprojectHandlers";
import type {
  U3130ExpenseItem,
  U3130EquipmentItem,
  U3130FindProjectItem,
  U3130FindSubprojectItem,
  U3130HonorariumItem,
  U3130HonorariumProjectRef,
  U3130Timesheet,
} from "../../U3130TimesheetModel";
import {
  u3129ClientBlockLabelFromProject,
  u3129ClientDisplayFromFindProject,
} from "../../../u3129Timesheet/tools/u3129BuildHonorariumLinesFromTasks";
import type {
  U3129FindProjectItem as U3129FindProjectItemForClientLabel,
  U3129HonorariumProjectRef as U3129HonorariumProjectRefForClientLabel,
} from "../../../u3129Timesheet/U3129Service";
import { U3130_READONLY_TABLE_CELL_BOX_CLASS, U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from "../../tools/u3130ReadonlyTableCellClass";
import { U3130_GRID_COMPACT_ROW_CELL_PY, U3130_GRID_COMPACT_TABLE_CONTROL_CLASS } from "../../tools/u3130TimesheetGridCompactStyles";

import { FaSearch } from "react-icons/fa";

export type U3130LineArrayField = "honoraria" | "expenses" | "equipment";

type LineItem = U3130HonorariumItem | U3130ExpenseItem | U3130EquipmentItem;

export type U3130ProjectSearchPanelProps = {
  panelFields?: Record<string, unknown>;
  setPanelFields: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onPanelSearch: (fields: Record<string, unknown>) => void;
  results?: Record<string, unknown>[];
  totalCount?: number;
  truncated?: boolean;
  setShowPanel: (show: boolean) => void;
  handleRowClickFn?: (item: Record<string, unknown>) => void;
  ns: string;
};

/** Panneau latéral de recherche projet (F3, tableau résultats) — partagé par les lignes feuille de temps U3130. */
export function U3130ProjectSearchPanel({
  panelFields = {},
  setPanelFields,
  onPanelSearch,
  results = [],
  totalCount,
  truncated,
  setShowPanel,
  handleRowClickFn,
  ns,
}: U3130ProjectSearchPanelProps) {
  const { t } = useTranslation();
  const [showResults, setShowResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const projetNumberRef = useRef<HTMLInputElement | null>(null);

  const list = Array.isArray(results) ? results : [];

  useEffect(() => {
    if (list.length > 0) {
      setShowResults(true);
      setHighlightedIndex(-1);
    } else {
      setShowResults(false);
      setHighlightedIndex(-1);
    }
  }, [list.length]);

  const handleCriteriaFocus = createPanelCriteriaFocusHandler(setHighlightedIndex);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowPanel(false);
        setShowResults(false);
      }
      if (e.key === "F3") {
        e.preventDefault();
        onPanelSearch(panelFields as Record<string, unknown>);
      }
    };
    window.addEventListener("keydown", handleKeyDown as EventListener);
    return () => window.removeEventListener("keydown", handleKeyDown as EventListener);
  }, [panelFields, onPanelSearch, setShowPanel]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (projetNumberRef.current) {
        try {
          projetNumberRef.current.focus();
        } catch {
          // ignore
        }
      }
    });
  }, []);

  const getFocusableElements = () => {
    if (!panelRef.current) return [] as HTMLElement[];
    const selectors = ["a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", '[tabindex]:not([tabindex="-1"])'];
    return Array.from(panelRef.current.querySelectorAll(selectors.join(","))) as HTMLElement[];
  };

  const handleTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const focusable = getFocusableElements().filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!panelRef.current?.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  };

  const handleRowClick = (item: Record<string, unknown>, idx: number) => {
    setShowPanel(false);
    setHighlightedIndex(-1);
    void idx;
    handleRowClickFn?.(item);
  };

  return (
    <div
      ref={panelRef}
      onKeyDown={handleTrapKeyDown}
      className={`flex h-full flex-col ${U2000_AUTOCOMPLETE_PANEL_COMPACT.rootGap} rounded-lg border border-gray-200 bg-white p-4 shadow-lg animate-fade-in`}
      tabIndex={0}
    >
      <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaToolbar}>
        <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaFields} onFocusCapture={handleCriteriaFocus}>
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaRow}>
            <div className="w-[140px] shrink-0">
              <U2000InputTextBase
                placeholder={t("lblProject", { ns, defaultValue: "Projet" })}
                fieldName="projetNumber"
                type="string"
                value={(panelFields.projetNumber as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, projetNumber: value ?? "" }))}
                inputRef={projetNumberRef}
              />
            </div>
            <div className="min-w-0 flex-2" style={{ minWidth: 180 }}>
              <U2000InputTextBase
                placeholder={t("lblProjetName", { ns, defaultValue: "Description projet" })}
                fieldName="projetName"
                type="string"
                value={(panelFields.projetName as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, projetName: value ?? "" }))}
              />
            </div>
          </div>
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaRow}>
            <div className="w-[140px] shrink-0">
              <U2000InputTextBase
                placeholder={t("lblCustomerCode", { ns, defaultValue: "Code client" })}
                fieldName="customerCode"
                type="string"
                value={(panelFields.customerCode as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, customerCode: value ?? "" }))}
              />
            </div>
            <div className="min-w-0 flex-1" style={{ minWidth: 120 }}>
              <U2000InputTextBase
                placeholder={t("lblCustomerName", { ns, defaultValue: "Nom client" })}
                fieldName="customerName"
                type="string"
                value={(panelFields.customerName as string) ?? ""}
                setValue={(value) => setPanelFields((prev) => ({ ...prev, customerName: value ?? "" }))}
              />
            </div>
          </div>
        </div>
        <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.searchRow}>
          <U2000ActionBtn
            className="w-full"
            label={t("btnSearch", { ns: "common", defaultValue: "Rechercher" })}
            icon={FaSearch}
            trailingHint={<U2000KeyboardKeyHint>F3</U2000KeyboardKeyHint>}
            handleClick={() => onPanelSearch(panelFields as Record<string, unknown>)}
          />
        </div>
      </div>
      <U2000AutoCompletePanelResultsBlock highlightedIndex={highlightedIndex}>
        <U2000AutoCompleteInputResultTable
          results={list}
          resultLabels={[t("lblProject", { ns, defaultValue: "Projet" }), t("lblProjetName", { ns, defaultValue: "Description" }), t("lblCustomerCode", { ns, defaultValue: "Code client" }), t("lblCustomerName", { ns, defaultValue: "Nom client" })]}
          resultFields={["projetNumber", "projetName", "customerCode", "customerName"]}
          handleRowClick={handleRowClick}
          showResults={showResults}
          setShowResults={setShowResults}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          showHeader={false}
          className={U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS}
          totalCount={totalCount}
          truncated={truncated}
        />
      </U2000AutoCompletePanelResultsBlock>
    </div>
  );
}

export interface U3130LineProjectSubprojectRowCellsProps {
  baseField: string;
  arrayField: U3130LineArrayField;
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3130Timesheet>;
  findProjectHandlers: ReturnType<typeof useU3130FindProjectHandlers>;
  getProjectId: () => string | undefined | null;
  readonly: boolean;
  rowIncludeSubProject: boolean;
  /** Personnalisation initLU `admIncludeSubProject === "true"` : affiche la colonne sous-projet. */
  showSubProjectColumn: boolean;
  /** Si true, réinitialise taskType / task (honoraires) lors d’un changement de projet ou sous-projet. */
  clearTaskFieldsOnProjectChange: boolean;
  /** Grilles compactes (Dépenses / Équipement, BL-0672 côté U3129) : boîtes et champs h-8. */
  compactTableControls?: boolean;
  ns: string;
}

/** Cellules tableau : projet, client (lecture seule), et optionnellement sous-projet — aligné sur l’onglet Honoraires. */
export function U3130LineProjectSubprojectRowCells({
  baseField,
  arrayField,
  index,
  formStateHandlers,
  findProjectHandlers,
  getProjectId,
  readonly,
  rowIncludeSubProject,
  showSubProjectColumn,
  clearTaskFieldsOnProjectChange,
  compactTableControls = false,
  ns,
}: U3130LineProjectSubprojectRowCellsProps) {
  const readonlyBoxClass = compactTableControls ? U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS : U3130_READONLY_TABLE_CELL_BOX_CLASS;
  const readonlyCellPy = compactTableControls ? U3130_GRID_COMPACT_ROW_CELL_PY : "py-1.5";
  const controlCellPy = compactTableControls ? U3130_GRID_COMPACT_ROW_CELL_PY : "py-2";
  const controlWrapClass = compactTableControls
    ? `text-left min-w-0 ${U3130_GRID_COMPACT_TABLE_CONTROL_CLASS} [&_input]:text-left`
    : "text-left [&_input]:text-left min-w-0";
  const findSubprojectHandlers = useU3130FindSubprojectHandlers({ getProjectId });

  const onSearchProject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      return findProjectHandlers.search(criterias);
    },
    [findProjectHandlers],
  );

  const onSearchSubproject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      const results = await findSubprojectHandlers.search(criterias);
      const list = Array.isArray(results) ? results : results.results;
      return list.map((r: U3130FindSubprojectItem) => ({ value: r.id, ...r }));
    },
    [findSubprojectHandlers],
  );

  const extractProject = useCallback(
    (result: U3130FindProjectItem | null): { fieldName: string; value: unknown }[] => {
      const clientDisplayValue = result ? u3129ClientDisplayFromFindProject(result as U3129FindProjectItemForClientLabel) : "";
      const taskClears: { fieldName: string; value: unknown }[] = clearTaskFieldsOnProjectChange
        ? [
            { fieldName: `${baseField}.taskType`, value: "" },
            { fieldName: `${baseField}.task`, value: null },
          ]
        : [];
      if (result) {
        return [
          { fieldName: `${baseField}.project`, value: result },
          { fieldName: `${baseField}.projectId`, value: result.id },
          { fieldName: `${baseField}.clientDisplay`, value: clientDisplayValue },
          { fieldName: `${baseField}.subprojectId`, value: null },
          { fieldName: `${baseField}.subproject`, value: null },
          ...taskClears,
        ];
      }
      return [
        { fieldName: `${baseField}.project`, value: null },
        { fieldName: `${baseField}.projectId`, value: null },
        { fieldName: `${baseField}.clientDisplay`, value: "" },
        { fieldName: `${baseField}.subprojectId`, value: null },
        { fieldName: `${baseField}.subproject`, value: null },
        ...taskClears,
      ];
    },
    [baseField, clearTaskFieldsOnProjectChange],
  );

  const extractSubproject = useCallback(
    (result: U3130FindSubprojectItem | null): { fieldName: string; value: unknown }[] => {
      const taskClears: { fieldName: string; value: unknown }[] = clearTaskFieldsOnProjectChange
        ? [
            { fieldName: `${baseField}.taskType`, value: "" },
            { fieldName: `${baseField}.task`, value: null },
          ]
        : [];
      if (result) {
        return [
          { fieldName: `${baseField}.subproject`, value: result },
          { fieldName: `${baseField}.subprojectId`, value: result.id },
          ...taskClears,
        ];
      }
      return [
        { fieldName: `${baseField}.subproject`, value: null },
        { fieldName: `${baseField}.subprojectId`, value: null },
        ...taskClears,
      ];
    },
    [baseField, clearTaskFieldsOnProjectChange],
  );

  const data = formStateHandlers.getData();
  const row = data?.[arrayField]?.[index] as LineItem | undefined;
  const rowRec = row as unknown as Record<string, unknown> | undefined;
  const projectDisplay = rowRec?.project as U3130FindProjectItem | U3130HonorariumProjectRef | undefined;
  const subprojectDisplay = rowRec?.subproject as U3130FindSubprojectItem | undefined;
  const shouldShowSubproject = rowIncludeSubProject;

  const projectRef = row?.project as U3130HonorariumProjectRef | U3130FindProjectItem | undefined;
  const findProj = projectDisplay && "customerName" in projectDisplay ? (projectDisplay as U3130FindProjectItem) : undefined;
  const clientFromProject = u3129ClientBlockLabelFromProject(
    (projectRef ?? findProj) as U3129FindProjectItemForClientLabel | U3129HonorariumProjectRefForClientLabel | undefined,
  );
  const rawClientDisplay = rowRec?.clientDisplay;
  const clientDisplayValue =
    clientFromProject ||
    (rawClientDisplay != null && String(rawClientDisplay).trim() !== "" ? String(rawClientDisplay).trim() : "") ||
    "—";
  const clientDisplayFallback = clientFromProject || "—";

  useEffect(() => {
    if (readonly) return;
    if (!clientDisplayFallback || clientDisplayFallback === "—") return;
    if (rawClientDisplay != null && String(rawClientDisplay).trim() !== "") return;
    const currentData = formStateHandlers.getData();
    const list = (currentData?.[arrayField] ?? []) as LineItem[];
    const currentRow = list[index];
    if (!currentRow) return;
    const currentValue = (currentRow as unknown as Record<string, unknown>).clientDisplay;
    if (currentValue != null && String(currentValue).trim() !== "") return;
    const updated = list.map((r, i) =>
      i === index ? ({ ...(r as unknown as Record<string, unknown>), clientDisplay: clientDisplayFallback } as LineItem) : r
    );
    formStateHandlers.setData({ ...currentData, [arrayField]: updated } as U3130Timesheet);
  }, [readonly, clientDisplayFallback, rawClientDisplay, formStateHandlers, index, arrayField]);

  if (readonly) {
    return (
      <>
        <td className={`pl-1 pr-3 ${readonlyCellPy} align-top w-40 text-left min-w-40`}>
          <div className={readonlyBoxClass}>
            {projectDisplay ? (projectDisplay.projetNumber ?? projectDisplay.id) : (row?.projectId ?? "—")}
          </div>
        </td>
        <td className={`px-3 ${readonlyCellPy} align-top text-left min-w-88`}>
          <div className={readonlyBoxClass}>
            {clientDisplayValue != null && String(clientDisplayValue).trim() ? String(clientDisplayValue) : "—"}
          </div>
        </td>
        {showSubProjectColumn ? (
          <td className={`px-3 ${readonlyCellPy} align-top w-48 text-left min-w-48`}>
            <div className={readonlyBoxClass}>
              {shouldShowSubproject
                ? subprojectDisplay
                  ? `${subprojectDisplay.subProjetNumber ?? ""} ${subprojectDisplay.subProjetName ?? ""}`.trim() || subprojectDisplay.id
                  : (row?.subprojectId ?? "—")
                : "—"}
            </div>
          </td>
        ) : null}
      </>
    );
  }

  return (
    <>
      <td className={`pl-1 pr-3 ${controlCellPy} align-top w-40 text-left min-w-40`}>
        <div className={controlWrapClass}>
          <U2000AutoCompleteInput
            pageSize={15}
            formStateHandlers={formStateHandlers}
            fieldName={`${baseField}.project`}
            mainFieldName="projetNumber"
            entityLink="U3121"
            label=""
            onSearch={onSearchProject}
            displayFn={(item) => {
              const p = item as U3130FindProjectItem;
              return p ? (p.projetNumber ?? p.id) : "";
            }}
            overrideExtractDataFn={(item) => extractProject(item as U3130FindProjectItem | null)}
            basicResultHeight={300}
            resultLabels={[]}
            resultFields={["projetNumber", "projetName"]}
            minCarLaunchSearch={3}
            customSearchPanelHeight={500}
            panelWidth={400}
            dropdownWidth={400}
            renderPanel={(props) => <U3130ProjectSearchPanel {...props} ns={ns} />}
            compactGrid={compactTableControls}
          />
        </div>
      </td>
      <td className={`px-3 ${controlCellPy} align-top text-left min-w-88`}>
        <div className={compactTableControls ? "min-w-0" : controlWrapClass}>
          {compactTableControls ? (
            <div className={readonlyBoxClass}>
              {clientDisplayValue != null && String(clientDisplayValue).trim() ? String(clientDisplayValue) : "—"}
            </div>
          ) : (
            <U2000InputText formStateHandlers={formStateHandlers} fieldName={`${baseField}.clientDisplay`} label="" readonly required={false} />
          )}
        </div>
      </td>
      {showSubProjectColumn ? (
        <td className={`px-3 ${controlCellPy} align-top w-48 text-left min-w-48`}>
          <div className={controlWrapClass}>
            <U2000AutoCompleteInput
              pageSize={15}
              formStateHandlers={formStateHandlers}
              fieldName={`${baseField}.subproject`}
              mainFieldName="subProjetNumber"
              label=""
              onSearch={onSearchSubproject}
              displayFn={(item) => {
                const sp = item as U3130FindSubprojectItem;
                return sp ? sp.subProjetNumber?.trim() || sp.id : "";
              }}
              overrideExtractDataFn={(item) => extractSubproject(item as U3130FindSubprojectItem | null)}
              basicResultHeight={300}
              resultLabels={[]}
              resultFields={["subProjetNumber", "subProjetName"]}
              minCarLaunchSearch={0}
              readonly={!getProjectId() || !rowIncludeSubProject}
              compactGrid={compactTableControls}
            />
          </div>
        </td>
      ) : null}
    </>
  );
}
